import { usePrevious } from '@lemonade-hq/cdk';
import { useI18nContext } from '@lemonade-hq/nuiza';
import type { Nullish } from '@lemonade-hq/ts-helpers';
import { TRIGGER_3DS_SESSION_ERROR } from '@lemonade-hq/uniclient-chat';
import { useToast } from '@lemonade-hq/uniclient-theme';
import { useEffect, useState } from 'react';

const expectedSessionErrors = [TRIGGER_3DS_SESSION_ERROR];
export const WIZARD_ERROR_TOAST_ID = 'wizard-error-toast';
export interface WizardErrorsProps {
  readonly currentStepIndex: number;
  readonly isSubmitting: boolean;
  readonly isInitializing: boolean;
  readonly persistenceError: Nullish | boolean | string;
  readonly onError: (errorMessage: string) => void;

  // a flag which tells whether the user changes any input clicking submission. relevant for displaying an error until the user tries to fix their input
  readonly hasNoChangeSinceSubmission: boolean;
  readonly isErrorFromLLMRequest: boolean;
}

export interface WizardErrorsContext {
  readonly currentError: Nullish | string;
  readonly setCustomError: (error?: string | null) => void;
}

export function useWizardErrors({
  currentStepIndex,
  isSubmitting,
  isInitializing,
  persistenceError,
  hasNoChangeSinceSubmission,
  isErrorFromLLMRequest,
  onError,
}: WizardErrorsProps): WizardErrorsContext {
  const { s } = useI18nContext();
  const { open: openToast, close: closeToast } = useToast();
  const [customError, setCustomError] = useState<Nullish | string>(null);

  // throws an error whenever there is a persistence-layer error
  useEffect(() => {
    if (isInitializing && persistenceError != null) {
      // to be caught by the error boundary
      throw new Error('Init chat failed');
    }
  }, [persistenceError, isInitializing]);

  let currentError: Nullish | string;

  if (isSubmitting) {
    currentError = null;
  } else if (hasNoChangeSinceSubmission || isErrorFromLLMRequest) {
    if (persistenceError != null) {
      currentError = typeof persistenceError === 'string' ? persistenceError : s('chat.error.general');
    }
  } else {
    currentError = customError;
  }

  const previousError = usePrevious(currentError, null);

  const shouldPersistError = currentError === previousError && isErrorFromLLMRequest;

  // clear custom error when step changes
  useEffect(() => {
    setCustomError(null);
  }, [currentStepIndex]);

  useEffect(() => {
    if (shouldPersistError) {
      return;
    }

    if (currentError != null && !expectedSessionErrors.includes(currentError)) {
      openToast({
        icon: 'alert-solid',
        title: currentError,
        timeout: Infinity,
        type: 'system',
        id: WIZARD_ERROR_TOAST_ID,
        action: {
          label: s('toast.dismiss'),
          onAction: () => closeToast(WIZARD_ERROR_TOAST_ID),
        },
      });
      onError(currentError);
    } else {
      closeToast(WIZARD_ERROR_TOAST_ID);
    }
  }, [
    currentError,
    currentStepIndex,
    isSubmitting,
    onError,
    openToast,
    closeToast,
    s,
    previousError,
    shouldPersistError,
  ]);

  return { currentError, setCustomError };
}
